import { useState, useEffect } from 'react';
import supabase from '../supabase';
import { theme, font, inputStyle, labelStyle, pillButton, outlineButton, ghostButton } from './adminTheme';
import { useAdminLang } from './AdminLangContext';
import '../design-system.css';

const T = {
    fr: {
        defaultButtonText: 'Explorer',
        heading: 'Pop-up du site',
        active: '· actif',
        desc: 'Un seul pop-up à la fois — affiché à tous les visiteurs.',
        imageOptional: 'Image (optionnelle)',
        uploading: 'Upload en cours...',
        title: 'Titre / Message',
        link: 'Lien (optionnel)',
        buttonText: 'Texte du bouton',
        save: 'Sauvegarder',
        saving: 'Sauvegarde...',
        activate: 'Activer sur le site',
        deactivate: 'Désactiver le pop-up',
        loading: 'Chargement...',
        uploadError: (msg) => `Erreur upload : ${msg}`,
        imageUploaded: 'Image uploadée',
        saveError: (msg) => `Erreur : ${msg}`,
        activated: 'Pop-up activé',
        savedDisabled: 'Sauvegardé (désactivé)',
    },
    en: {
        defaultButtonText: 'Explore',
        heading: 'Site pop-up',
        active: '· active',
        desc: 'One pop-up at a time — shown to every visitor.',
        imageOptional: 'Image (optional)',
        uploading: 'Uploading...',
        title: 'Title / Message',
        link: 'Link (optional)',
        buttonText: 'Button text',
        save: 'Save',
        saving: 'Saving...',
        activate: 'Activate on the site',
        deactivate: 'Deactivate pop-up',
        loading: 'Loading...',
        uploadError: (msg) => `Upload error: ${msg}`,
        imageUploaded: 'Image uploaded',
        saveError: (msg) => `Error: ${msg}`,
        activated: 'Pop-up activated',
        savedDisabled: 'Saved (disabled)',
    },
};

function AdminPopups() {
    const { lang } = useAdminLang();
    const t = T[lang] || T.fr;
    const [form, setForm] = useState({ title: '', image_url: '', link_url: '', button_text: t.defaultButtonText, enabled: false });
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [statusMsg, setStatusMsg] = useState('');
    const [loading, setLoading] = useState(true);

    const fetchPopup = async () => {
        setLoading(true);
        const { data } = await supabase.from('popup').select('*').single();
        if (data) {
            setForm({
                title: data.title || '',
                image_url: data.image_url || '',
                link_url: data.link_url || '',
                button_text: data.button_text || t.defaultButtonText,
                enabled: data.enabled || false,
            });
        }
        setLoading(false);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchPopup(); }, []);

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        const fileName = `popup-${Date.now()}.${file.name.split('.').pop()}`;
        const { error } = await supabase.storage.from('covers').upload(fileName, file, { upsert: true });
        if (error) setStatusMsg(t.uploadError(error.message));
        else {
            const { data: urlData } = supabase.storage.from('covers').getPublicUrl(fileName);
            setForm(f => ({ ...f, image_url: urlData.publicUrl }));
            setStatusMsg(t.imageUploaded);
        }
        setUploading(false);
    };

    const handleSave = async (publish) => {
        setSaving(true);
        const payload = {
            title: form.title,
            image_url: form.image_url,
            link_url: form.link_url || null,
            button_text: form.button_text || t.defaultButtonText,
            enabled: publish,
        };

        const { data: existing } = await supabase.from('popup').select('*').limit(1).maybeSingle();

        let error;
        if (existing) ({ error } = await supabase.from('popup').update(payload).eq('id', existing.id));
        else ({ error } = await supabase.from('popup').insert(payload));

        if (error) setStatusMsg(t.saveError(error.message));
        else {
            setForm(f => ({ ...f, enabled: publish }));
            setStatusMsg(publish ? t.activated : t.savedDisabled);
        }
        setSaving(false);
    };

    if (loading) return <p style={{ color: theme.textMuted, fontFamily: font, fontSize: '12px' }}>{t.loading}</p>;

    return (
        <div style={{ maxWidth: '400px' }}>
            <p style={{ ...labelStyle, marginBottom: '4px' }}>
                {t.heading} {form.enabled && <span style={{ color: theme.textMuted }}>{t.active}</span>}
            </p>
            <p style={{ fontFamily: font, fontSize: '11px', color: theme.textMuted, margin: '0 0 20px' }}>
                {t.desc}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                    <label style={labelStyle}>{t.imageOptional}</label>
                    {form.image_url && (
                        <div style={{ width: '100%', maxWidth: '260px', borderRadius: 0, overflow: 'hidden', marginBottom: '8px', background: theme.bg }}>
                            <img src={form.image_url} alt="preview" style={{ width: '100%', display: 'block' }} />
                        </div>
                    )}
                    <input type="file" accept="image/*" onChange={handleUpload} disabled={uploading} style={{ fontSize: '11px', color: theme.textMuted, fontFamily: font }} />
                    {uploading && <p style={{ fontSize: '10px', color: theme.textMuted, margin: '4px 0 0' }}>{t.uploading}</p>}
                </div>

                <div>
                    <label style={labelStyle}>{t.title}</label>
                    <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} style={inputStyle} />
                </div>

                <div>
                    <label style={labelStyle}>{t.link}</label>
                    <input value={form.link_url} onChange={e => setForm(f => ({ ...f, link_url: e.target.value }))} style={inputStyle} />
                </div>

                {form.link_url && (
                    <div>
                        <label style={labelStyle}>{t.buttonText}</label>
                        <input value={form.button_text} onChange={e => setForm(f => ({ ...f, button_text: e.target.value }))} style={inputStyle} />
                    </div>
                )}

                <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                    <button className="wlink" onClick={() => handleSave(false)} disabled={saving} style={outlineButton}>{t.save}</button>
                    <button className="wlink" onClick={() => handleSave(true)} disabled={saving} style={pillButton}>
                        {saving ? t.saving : t.activate}
                    </button>
                </div>

                {form.enabled && <button className="wlink" onClick={() => handleSave(false)} style={ghostButton}>{t.deactivate}</button>}

                {statusMsg && <p style={{ fontSize: '11px', color: theme.textMuted, fontFamily: font, margin: 0 }}>{statusMsg}</p>}
            </div>
        </div>
    );
}

export default AdminPopups;